// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import notification from 'antd/lib/notification';
import message from 'antd/lib/message';
import { DownOutlined } from '@ant-design/icons';

import {
    Job, MLModel, getCore,
} from 'cvat-core-wrapper';
import { CombinedState } from 'reducers';
import { getModelsAsync } from 'actions/models-actions';
import Menu, { MenuInfo } from 'components/dropdown-menu';
import CVATTooltip from 'components/common/cvat-tooltip';

const core = getCore();

interface Props {
    job: Job;
}

function JobPrecomputeButton(props: Props): JSX.Element | null {
    const { job } = props;
    const dispatch = useDispatch();
    const interactors = useSelector((state: CombinedState) => state.models.interactors);
    const modelsFetching = useSelector((state: CombinedState) => state.models.fetching);
    const modelsInitialized = useSelector((state: CombinedState) => state.models.initialized);
    const [requestID, setRequestID] = useState<string | null>(null);
    const [progress, setProgress] = useState<number>(0);

    useEffect(() => {
        if (!modelsInitialized && !modelsFetching) {
            dispatch(getModelsAsync());
        }
    }, [modelsInitialized, modelsFetching]);

    const supported = useMemo(
        () => interactors.filter((m) => m.params?.canvas?.supportsPrecompute),
        [interactors],
    );

    const start = useCallback(async (model: MLModel) => {
        try {
            const rid = await core.lambda.run(job.taskId, model, {
                job: job.id,
                precompute: true,
            });
            setRequestID(rid);
            setProgress(0);

            await core.lambda.listen(
                rid,
                model.id,
                (status: string, pct: number, statusMessage?: string) => {
                    if (status === 'finished') {
                        setRequestID(null);
                        setProgress(0);
                        message.success(`Feature cache warmed for job #${job.id} (${model.name}).`);
                    } else if (status === 'failed') {
                        setRequestID(null);
                        setProgress(0);
                        notification.error({
                            message: `Precompute failed for job #${job.id}`,
                            description: statusMessage || 'See server logs for details.',
                        });
                    } else {
                        setProgress(pct);
                    }
                },
            );
        } catch (error: any) {
            setRequestID(null);
            setProgress(0);
            notification.error({
                message: 'Could not start precompute',
                description: error.toString(),
            });
        }
    }, [job.id, job.taskId]);

    if (!supported.length) return null;

    const running = requestID !== null;
    const tooltip = 'Run the interactor over every frame in this job to warm its feature cache, '
        + 'so clicks in the annotation page respond instantly.';

    if (supported.length === 1) {
        const model = supported[0];
        return (
            <CVATTooltip title={tooltip}>
                <Button
                    size='small'
                    loading={running}
                    disabled={running}
                    onClick={() => start(model)}
                >
                    {running ? `Precomputing ${progress}%` : `Precompute features (${model.name})`}
                </Button>
            </CVATTooltip>
        );
    }

    const overlay = (
        <Menu
            onClick={(info: MenuInfo) => {
                const chosen = supported.find((m) => m.id === info.key);
                if (chosen) start(chosen);
            }}
        >
            {supported.map((m) => (
                <Menu.Item key={m.id}>{m.name}</Menu.Item>
            ))}
        </Menu>
    );

    return (
        <CVATTooltip title={tooltip}>
            <Dropdown overlay={overlay} trigger={['click']} disabled={running}>
                <Button size='small' loading={running}>
                    {running ? `Precomputing ${progress}%` : 'Precompute features'}
                    {' '}
                    <DownOutlined />
                </Button>
            </Dropdown>
        </CVATTooltip>
    );
}

export default React.memo(JobPrecomputeButton);
